/**
 * components/TopBar — the persistent header: brand, a live/poll connection dot, the
 * live Equity + daily %, and the always-reachable KILL SWITCH (PLAN §5). Equity + the
 * daily figure are fed from the shell's real-time feed; a demo flag is shown honestly
 * when the backend is serving seed data.
 */
import { directionClass, money, pct } from '../lib/format';
import type { Transport } from '../api/useLive';
import { KillSwitch } from './KillSwitch';

interface TopBarProps {
  readonly equity: number | null;
  readonly dailyPct: number | null;
  readonly halted: boolean;
  readonly demo: boolean;
  readonly transport: Transport;
}

const TRANSPORT_LABEL: Record<Transport, string> = {
  connecting: 'Connecting…',
  stream: 'LIVE',
  poll: 'Polling',
};

export function TopBar({ equity, dailyPct, halted, demo, transport }: TopBarProps): JSX.Element {
  return (
    <header className="topbar">
      <div className="topbar__brand">
        <span className="topbar__logo">TK</span>
        <span className="topbar__brand-name">Trade Kharvyn</span>
      </div>

      <span
        className={`conn conn--${transport}`}
        title={transport === 'stream' ? 'Real-time stream connected' : TRANSPORT_LABEL[transport]}
      >
        <span className="conn__dot" aria-hidden />
        <span className="conn__label">{TRANSPORT_LABEL[transport]}</span>
      </span>

      {demo && <span className="demo-flag" title="Backend is serving demo/seed data">demo</span>}

      <div className="topbar__equity">
        <span className="topbar__equity-label">Equity</span>
        <span className="topbar__equity-value num">{equity == null ? '—' : money(equity, 0)}</span>
        {dailyPct != null && (
          <span className={`topbar__equity-delta num ${directionClass(dailyPct)}`}>{pct(dailyPct)}</span>
        )}
      </div>

      <KillSwitch halted={halted} />
    </header>
  );
}
